import React, { useMemo } from 'react';
import { transformCustomImageTags } from '../utils/markdownUtils';
import { 
  Box, 
  Text, 
  Code, 
  Link, 
  List, 
  ListItem, 
  Heading, 
  Divider, 
  Alert,
  AlertIcon,
  Kbd,
  chakra,
  useColorMode
} from '@chakra-ui/react';
import ErrorBoundary from './ErrorBoundary';

// Defer require so CRA build works and Jest can mock the module
// eslint-disable-next-line @typescript-eslint/no-var-requires
const rm = require('react-markdown');
const ReactMarkdown = rm.default || rm;

// Custom fallback for markdown rendering errors
const MarkdownErrorFallback = () => (
  <Alert status="error" borderRadius="md" mt={2}>
    <AlertIcon />
    There was an error rendering this content.
  </Alert>
);

interface MarkdownRendererProps {
  content: string;
  isUser?: boolean;
}

// Define components outside the main component to avoid re-creation
const createComponents = (isUser: boolean, colorMode: 'light' | 'dark') => ({
  // Text formatting
  p: ({ node, ...props }: any) => (
    <Text 
      mb={4} 
      lineHeight="tall"
      dir="ltr"
      style={{ direction: 'ltr', unicodeBidi: 'plaintext' }}
      textAlign="left"
      color={isUser ? 'inherit' : undefined}
      {...props} 
    />
  ),
  
  // Headers
  h1: ({ node, ...props }: any) => (
    <Heading as="h1" size="xl" my={4} color={isUser ? 'inherit' : 'gray.800'} dir="ltr" textAlign="left" {...props} />
  ),
  h2: ({ node, ...props }: any) => (
    <Heading as="h2" size="lg" my={3} color={isUser ? 'inherit' : 'gray.800'} dir="ltr" textAlign="left" {...props} />
  ),
  h3: ({ node, ...props }: any) => (
    <Heading as="h3" size="md" my={2} color={isUser ? 'inherit' : 'gray.800'} dir="ltr" textAlign="left" {...props} />
  ),
  
  // Inline elements
  strong: ({ node, ...props }: any) => <Text as="strong" fontWeight="bold" dir="ltr" {...props} />,
  em: ({ node, ...props }: any) => <Text as="em" fontStyle="italic" dir="ltr" {...props} />,
  del: ({ node, ...props }: any) => <Text as="del" textDecoration="line-through" dir="ltr" {...props} />,
  
  // Code blocks
  code: ({ node, inline, className, children, ...props }: any) => {
    const codeBg = colorMode === 'light' ? 'gray.100' : 'gray.800';
    
    if (inline) {
      return <Code p={1} borderRadius="md" dir="ltr" {...props}>{children}</Code>;
    }
    
    return (
      <Box 
        as="pre" 
        p={4} 
        my={4} 
        borderRadius="md" 
        bg={codeBg}
        overflowX="auto"
        dir="ltr"
      >
        <Code 
          p={0} 
          bg="transparent"
          whiteSpace="pre-wrap"
          dir="ltr"
          {...props}
        >
          {children}
        </Code>
      </Box>
    );
  },
  
  // Lists
  ul: ({ node, ordered, ...props }: any) => (
    <List 
      styleType="disc" 
      pl={5} 
      my={2}
      dir="ltr"
      textAlign="left"
      {...props} 
    />
  ),
  ol: ({ node, ordered, ...props }: any) => (
    <List 
      as="ol" 
      styleType="decimal" 
      pl={5} 
      my={2}
      dir="ltr"
      textAlign="left"
      {...props} 
    />
  ),
  li: ({ node, ordered, ...props }: any) => {
    // Remove the ordered prop to prevent React warning
    // The ordered prop is used by react-markdown to distinguish between ordered and unordered lists
    // but we don't need it in our ListItem component
    return (
      <ListItem 
        mb={1}
        dir="ltr"
        textAlign="left"
        {...props} 
      />
    );
  },
  
  // Links
  a: ({ node, ...props }: any) => (
    <Link 
      color="#C200FB" 
      textDecoration="underline"
      _hover={{ color: '#A500D9' }}
      dir="ltr"
      {...props} 
    />
  ),
  
  // Horizontal rule
  hr: ({ node, ...props }: any) => (
    <Divider 
      my={4} 
      borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
      dir="ltr"
      {...props} 
    />
  ),
  
  // Blockquote
  blockquote: ({ node, ...props }: any) => (
    <Box 
      as="blockquote" 
      pl={4} 
      borderLeft="4px" 
      borderColor="#C200FB" 
      my={4}
      dir="ltr"
      {...props} 
    />
  ),
  
  // Keyboard input
  kbd: ({ node, ...props }: any) => (
    <Kbd 
      bg={colorMode === 'light' ? 'gray.100' : 'gray.700'}
      px={2}
      py={1}
      borderRadius="md"
      dir="ltr"
      {...props} 
    />
  ),
  
  // Inline code
  inlineCode: ({ node, ...props }: any) => (
    <Code 
      colorScheme="purple" 
      px={1.5} 
      py={0.5} 
      borderRadius="md"
      dir="ltr"
      {...props} 
    />
  ),
  
  // Images
  img: ({ node, src, ...props }: any) => {
    // If src is a relative path (no protocol), prepend the Supabase storage URL
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const storageUrl = supabaseUrl ? `${supabaseUrl}/storage/v1/object/public/course-images` : '';
    
    let imageSrc = src;
    if (src && !src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
      // Remove leading slash if present
      const cleanSrc = src.startsWith('/') ? src.substring(1) : src;
      imageSrc = `${storageUrl}/${cleanSrc}`;
    }
    
    return (
      <chakra.img 
        src={imageSrc}
        maxW="100%" 
        h="auto" 
        borderRadius="md" 
        my={4}
        mx="auto"
        display="block"
        dir="ltr"
        {...props} 
      />
    );
  }
});

const MarkdownRenderer = ({ 
  content, 
  isUser = false 
}: MarkdownRendererProps) => {
  const { colorMode } = useColorMode();
  const components = useMemo(
    () => createComponents(isUser, colorMode),
    [isUser, colorMode]
  );

  // Transform any custom image tags in the markdown
  const processedContent = useMemo(() => {
    try {
      return transformCustomImageTags(content);
    } catch (error) {
      console.error('Error processing markdown content:', error);
      return content; // Return original content if processing fails
    }
  }, [content]);

  return (
    <ErrorBoundary 
      fallback={<MarkdownErrorFallback />}
      context="markdown renderer"
    >
      <Box className="markdown-content">
        <ReactMarkdown components={components}>
          {processedContent}
        </ReactMarkdown>
      </Box>
    </ErrorBoundary>
  );
};

export default MarkdownRenderer;
